import {
  type RelativeDateRange,
  relativeDateRangeToDays,
} from "@/lib/utils/dateUtils";
import type { FilterExclusion } from "@/types/shared";
import type { TanStackRouteSearch } from "@/types/url-params.types";

/**
 * Converts a string to camelCase
 * Examples: "KEY_MESSAGE" -> "keyMessage", "SOME_FIELD_NAME" -> "someFieldName"
 */
const toCamelCase = (str: string): string => {
  return str
    .toLowerCase()
    .split("_")
    .map((word, index) => {
      if (index === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join("");
};

/**
 * Converts camelCase to UPPERCASE_SNAKE_CASE.
 * Examples: "keyMessage" -> "KEY_MESSAGE", "narrativeCluster" -> "NARRATIVE_CLUSTER"
 * @returns "" when `str` is null or undefined (defensive for untyped callers).
 */
export const toUpperCaseSnakeCase = (
  str: string | null | undefined,
): string => {
  if (str == null) return "";
  return str
    .replace(/([A-Z])/g, "_$1")
    .toUpperCase()
    .replace(/^_/, ""); // Remove leading underscore if present
};

/**
 * Type for the AND/OR filter structure returned by {@link buildAndFromSelected}
 * Supports nested AND/OR to match GraphQL FeedArticleFilter structure
 */
export type SelectedFiltersInput = {
  AND: Array<
    | {
        OR: Array<Record<string, string | string[]>>;
      }
    | {
        AND: Array<Record<string, string | string[]>>;
      }
    | Record<string, string | string[]>
  >;
};

/**
 * Builds a GraphQL-compatible AND/OR filter clause from a selection map.
 * Keys are converted to camelCase to match server-side field names (e.g., "KEY_MESSAGE" -> "keyMessage").
 * @param selected - Map or Record of selected filters
 * @param andCategories - Optional set of category keys that should use AND logic instead of OR
 */
export const buildAndFromSelected = <K extends string>(
  selected:
    | Map<K, string[]>
    | Record<K, string[]>
    | Partial<Record<K, string[]>>,
  andCategories?: Set<K> | K[] | Set<string> | string[],
): SelectedFiltersInput => {
  const entries =
    selected instanceof Map
      ? (Array.from(selected.entries()) as Array<[K, string[]]>)
      : (Object.entries(selected) as Array<[K, string[] | undefined]>).map(
          ([k, v]) => [k, v ?? []] as [K, string[]],
        );

  const andCategoriesSet =
    andCategories instanceof Set
      ? andCategories
      : andCategories
        ? new Set(andCategories)
        : new Set<K>();

  const AND = entries
    .filter(([, values]) => Array.isArray(values) && values.length > 0)
    .map(([key, values]) => {
      const useAnd = andCategoriesSet.has(key);
      const filterObjects = values.map((value) => ({
        [toCamelCase(key)]: value,
      }));

      return useAnd
        ? ({ AND: filterObjects } as const)
        : ({ OR: filterObjects } as const);
    });

  return { AND };
};

/**
 * Converts a flat filter object (with camelCase keys) to a Map with UPPERCASE_SNAKE_CASE keys.
 * Used when consuming flat filters from saved views (asFlat: true).
 */
export const flatFiltersToMap = <K extends string = string>(
  flatFilters: Record<string, string | string[]>,
): Map<K, string[]> => {
  const result = new Map<K, string[]>();

  for (const [camelKey, value] of Object.entries(flatFilters)) {
    // Skip non-dimension metadata: dateRange/period handled separately,
    // state is a scalar filter field (defaults to ADDED), not a sidebar category
    if (
      camelKey === "dateRange" ||
      camelKey === "period" ||
      camelKey === "state"
    )
      continue;

    const upperKey = toUpperCaseSnakeCase(camelKey) as K;
    const values = Array.isArray(value) ? value : [value];
    const existing = result.get(upperKey) ?? [];

    // Merge and deduplicate values
    for (const v of values) {
      if (!existing.includes(v)) existing.push(v);
    }

    if (existing.length > 0) {
      result.set(upperKey, existing);
    }
  }

  return result;
};

/**
 * Maps flat saved-view `feedArticleFilter.keyMessageIsAnd` to key-message filter exclusion.
 * When the field is missing or not a boolean, returns OR.
 */
export const getKeyMessageFilterExclusionFromFlatSavedView = (
  feedArticleFilter: unknown,
): FilterExclusion => {
  if (feedArticleFilter === null || typeof feedArticleFilter !== "object") {
    return "OR";
  }
  const { keyMessageIsAnd } = feedArticleFilter as {
    keyMessageIsAnd?: unknown;
  };
  if (typeof keyMessageIsAnd === "boolean") {
    return keyMessageIsAnd ? "AND" : "OR";
  }
  return "OR";
};

/**
 * Normalizes category names for display purposes
 * - SCORE_BUCKETS → IMPACT_SCORE
 * - SOCIAL_BUCKETS → SOCIAL_ENGAGEMENT
 * - Removes _BUCKET or _BUCKETS suffix from other categories
 */
export const normalizeCategoryForDisplay = (category: string): string => {
  if (category === "SCORE_BUCKETS") {
    return "IMPACT_SCORE";
  }
  if (category === "SOCIAL_BUCKETS") {
    return "SOCIAL_ENGAGEMENT";
  }
  if (category === "PUBLISHER_MEDIA_LIST_ID") {
    return "MEDIA_LIST";
  }
  if (category === "PUBLISHER_MEDIA_LIST_GROUP_ID") {
    return "MEDIA_LIST_GROUP";
  }
  if (category === "KEY_MESSAGE") {
    return "KEY_TOPICS";
  }
  // Remove _BUCKET or _BUCKETS suffix from any other category
  return category.replace(/_BUCKETS?$/, "");
};

/**
 * Input format from saved view's feedArticleFilter
 */
export interface SavedViewFilter {
  dateRange?: {
    startDate?: string;
    endDate?: string;
  };
  period?: RelativeDateRange;
  keyMessageIsAnd?: boolean;
  [key: string]:
    | string
    | string[]
    | boolean
    | { startDate?: string; endDate?: string }
    | RelativeDateRange
    | undefined;
}

/**
 * Partial TanStack route search params (for building search params incrementally)
 */
export type PartialRouteSearch = Partial<
  Omit<TanStackRouteSearch, "selected">
> & {
  selected: Record<string, string[]>;
};

/**
 * Converts a saved view's feedArticleFilter to TanStack Router search params.
 * - `period` (LAST_7_DAYS) → `filterDays` (7)
 * - `dateRange` → `startDate`/`endDate` (only when period is not present)
 * - flat filters (camelCase) → `selected` (UPPERCASE keys)
 */
export const savedViewFilterToSearchParams = (
  filter: SavedViewFilter | null | undefined,
): PartialRouteSearch => {
  if (!filter) {
    return { selected: {} };
  }

  const {
    dateRange,
    period,
    keyMessageIsAnd: _keyMessageIsAnd,
    ...restFilters
  } = filter;
  const result: PartialRouteSearch = { selected: {} };

  // Handle period (takes precedence over dateRange)
  if (period) {
    const days = relativeDateRangeToDays(period);
    if (days !== undefined) {
      result.filterDays = days;
    }
  } else if (dateRange?.startDate && dateRange?.endDate) {
    // Only use dateRange if period is not present
    result.startDate = dateRange.startDate;
    result.endDate = dateRange.endDate;
  }

  // Convert flat filters to selected (UPPERCASE keys)
  const flatFilters = restFilters as Record<string, string | string[]>;
  const selectedMap = flatFiltersToMap(flatFilters);

  // Convert Map to Record
  for (const [key, values] of selectedMap) {
    result.selected[key] = values;
  }

  return result;
};
